<script setup>
import { computed, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useVersionCheck } from '@/service/versionCheck';

const { updateAvailable, newVersion, currentVersion, reload, dismiss } = useVersionCheck();

// Emit toast notification when update is available
const toast = useToast();

watch(updateAvailable, (isAvailable) => {
    if (isAvailable) {
        toast.add({
            severity: 'info',
            summary: 'Update Available',
            detail: 'A new version of the application is available. Click to refresh.',
            life: null, // Don't auto-dismiss
            group: 'version-update'
        });
    }
});

const showBanner = computed(() => updateAvailable.value);
</script>

<template>
    <!-- Top banner notification -->
    <Transition name="slide-down">
        <div v-if="showBanner" class="update-notification-banner">
            <div class="flex items-center justify-between px-4 py-2">
                <div class="flex items-center">
                    <i class="pi pi-info-circle mr-2 text-xl" />
                    <span class="font-medium">A new version is available!</span>
                    <span v-if="newVersion" class="ml-2 text-sm opacity-80"> ({{ currentVersion }} → {{ newVersion }}) </span>
                </div>
                <div class="flex items-center gap-2">
                    <Button label="Refresh Now" icon="pi pi-refresh" class="p-button-sm p-button-rounded" @click="reload" />
                    <Button icon="pi pi-times" class="p-button-sm p-button-rounded p-button-text p-button-plain" @click="dismiss" v-tooltip.bottom="'Dismiss for this session'" />
                </div>
            </div>
        </div>
    </Transition>

    <!-- Alternative: Modal dialog -->
    <Dialog v-model:visible="showBanner" :modal="false" :closable="false" position="top" :draggable="false" :style="{ width: '450px', marginTop: '2rem' }" :breakpoints="{ '640px': '90vw' }" v-if="false">
        <template #header>
            <div class="flex items-center">
                <i class="pi pi-download mr-2" />
                <span class="font-semibold">Application Update</span>
            </div>
        </template>

        <div class="flex flex-col gap-3">
            <div class="flex items-center">
                <i class="pi pi-check-circle text-green-500 text-2xl mr-3" />
                <div>
                    <div class="font-medium">New version available</div>
                    <div class="text-sm text-muted-color">Version {{ newVersion }} is ready to install</div>
                </div>
            </div>

            <div class="bg-surface-100 dark:bg-surface-800 rounded-border p-3">
                <div class="text-sm mb-2 font-medium">What's new:</div>
                <ul class="m-0 pl-3 text-sm">
                    <li>Bug fixes and performance improvements</li>
                    <li>Enhanced user experience</li>
                </ul>
            </div>

            <div class="text-sm text-muted-color">
                <i class="pi pi-info-circle mr-1" />
                The page will refresh to apply the update. Please save any unsaved work.
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button label="Later" class="p-button-text" @click="dismiss" />
                <Button label="Refresh Now" icon="pi pi-refresh" @click="reload" autofocus />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.update-notification-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-600) 100%);
    color: white;
    z-index: 9999;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.update-notification-banner .p-button {
    color: white;
    border-color: rgba(255, 255, 255, 0.3);
}

.update-notification-banner .p-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
}

.update-notification-banner .p-button.p-button-text {
    color: white;
}

.update-notification-banner .p-button.p-button-text:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

/* Animations */
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-enter-from {
    transform: translateY(-100%);
    opacity: 0;
}

.slide-down-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}

/* Adjust main content when banner is shown */
.update-notification-banner ~ * {
    padding-top: 48px;
    transition: padding-top 0.3s ease;
}
</style>
